import { writable, type Readable } from 'svelte/store';
import { dev, browser } from '$app/environment';

type RemovableWithId<T> = T & { id: number };
interface Removable<T> extends Readable<T[]> {
  /** Push an item of `T` into the store. This also increments the ID counter */
  push(item: T): RemovableWithId<T>;
  /** Removes an item returned from `push` from the store  */
  remove(item: RemovableWithId<T>): void;
  removeAll(): void;
}

export function createRemovableStore<T>(ssrEnabled = false): Removable<T> {
  const { subscribe, update } = writable([] as RemovableWithId<T>[]);
  let genId = 0;
  return {
    subscribe,
    push: item => {
      const data = { ...item, id: genId++ } as RemovableWithId<T>;
      // Return the expected data directly on the server
      if (!ssrEnabled && !browser) {
        // Log a warning to the developer that they are executing code within the server context
        // that really shouldn't be executed there
        if (dev) {
          console.warn('You are calling `Toast.push` on the server. This should only be called on the client.');
        }
        return data;
      }
      update(value => [...value, data]);
      return data;
    },
    remove: item => {
      // no-op on server context
      if (!ssrEnabled && !browser) {
        return;
      }
      update(value => value.filter(x => x.id !== item.id));
    },
    removeAll: () => {
      update(value => []);
    }
  };
}
